import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useSetRecoilState } from 'recoil';
import { formatNumber } from '../../../utils/formatNumber';
import DrillDownView from '../AdminDashboard/DrillDownView';
import { TeamTreeVersionAtom } from '../../recoil/teamAtoms';
import {
  getTeamTree,
  addCategory,
  updateCategory,
  deleteCategory,
  addItem,
  updateItem,
  deleteItem,
  uploadImageToFileServer,
} from '../../utils/teamApi';

// ============== 스타일 (AdminDashboard 와 동일) ==============

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${p => p.theme.bgOverlay};
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  background: ${p => p.theme.bgCard};
  width: 95vw;
  max-width: 1280px;
  height: 92vh;
  border-radius: ${p => p.theme.radiusLg};
  box-shadow: ${p => p.theme.shadowLg};
  border: 1px solid ${p => p.theme.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: 100vh;
    max-width: none;
    border-radius: 0;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${p => p.theme.accent} 0%, #6d4ba2 100%);
  color: #fff;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;

  @media screen and (max-width: 480px) {
    font-size: 14px;
    gap: 4px;
  }
`;

const TitleIcon = styled.span`
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const TitleSub = styled.span`
  font-size: 12px;
  font-weight: 500;
  opacity: 0.85;
  margin-left: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  @media screen and (max-width: 380px) {
    font-size: 10px;
    margin-left: 0;
  }
`;

const RoleBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  background: rgba(255,255,255,0.18);
  color: #fff;
  letter-spacing: 0.4px;
`;

const CloseX = styled.button`
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: ${p => p.theme.transition};

  &:hover {
    background: rgba(255,255,255,0.3);
  }
`;

const InfoBar = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 12px;
  padding: 10px 24px;
  border-bottom: 1px solid ${p => p.theme.border};

  @media screen and (max-width: 768px) {
    padding: 10px 14px;
  }
`;

const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 320px 1fr 360px;
  gap: 0;
  overflow: hidden;
  background: ${p => p.theme.bg};

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-auto-rows: min-content;
    overflow-y: auto;
  }
`;

const Column = styled.section`
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bg};
  overflow: hidden;

  &:last-child {
    border-right: none;
  }

  @media screen and (max-width: 1024px) {
    border-right: none;
    border-bottom: 1px solid ${p => p.theme.border};
    overflow: visible;
  }
`;

const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
`;

const ColumnTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountBadge = styled.span`
  font-size: 11px;
  color: ${p => p.theme.accent};
  background: ${p => p.theme.accentLight};
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
`;

const PrimaryAddButton = styled.button`
  background: ${p => p.theme.accent};
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.accentHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OutlineAddButton = styled.button`
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.accent};
  border: 1px solid ${p => p.theme.accent};
  padding: 6px 12px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${p => p.theme.accentLight};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  font-size: 12px;
  box-sizing: border-box;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const SelectableCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: ${p => p.$selected ? p.theme.bgSelected : p.theme.bgCard};
  border: 1px solid ${p => p.$selected ? p.theme.borderSelected : p.theme.border};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  margin-bottom: 6px;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${p => p.theme.accent};
  }
`;

const FolderIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${p => p.$selected ? p.theme.accent : p.theme.bgInput};
  color: ${p => p.$selected ? '#fff' : p.theme.textSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.text};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDesc = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardCount = styled.span`
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  font-weight: 600;
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  ${p => p.$variant === 'start' && `
    background: #d1fae5;
    color: #047857;
  `}
  ${p => p.$variant === 'orphan' && `
    background: #fef3c7;
    color: #b45309;
  `}
`;

const InfoStrip = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 11px;
  padding: 8px 12px;
  border-radius: ${p => p.theme.radiusSm};
  margin-bottom: 10px;
  line-height: 1.5;

  strong { color: ${p => p.theme.text}; }
  span.warn { color: #b45309; font-weight: 600; }
`;

const FullAddButton = styled.button`
  width: 100%;
  padding: 10px;
  border: 1px dashed ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.textSecondary};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: ${p => p.theme.transition};

  &:hover {
    border-color: ${p => p.theme.accent};
    color: ${p => p.theme.accent};
    background: ${p => p.theme.accentLight};
  }
`;

const DragHint = styled.div`
  text-align: center;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  padding: 12px;
  border-top: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
`;

const SectionGroup = styled.div`
  margin-bottom: 6px;
`;

const SectionGroupLabel = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  font-weight: 600;
  padding: 8px 4px 6px;
`;

// 우측 상세 패널
const DetailHeaderActions = styled.div`
  display: flex;
  gap: 6px;
`;

const SmallActionButton = styled.button`
  border: 1px solid ${p => p.$danger ? p.theme.error : p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.$danger ? p.theme.error : p.theme.textSecondary};
  padding: 5px 10px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: ${p => p.theme.transition};

  &:hover:not(:disabled) {
    background: ${p => p.$danger ? '#fee2e2' : p.theme.bgHover};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DetailCard = styled.div`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  margin-bottom: 12px;
  overflow: hidden;
`;

const DetailCardTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.theme.text};
  padding: 12px 14px;
  border-bottom: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgInput};
`;

const DetailGrid = styled.div`
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 80px 1fr;
  row-gap: 8px;
  column-gap: 12px;
  font-size: 12px;
`;

const DetailKey = styled.span`
  color: ${p => p.theme.textMuted};
`;

const DetailValue = styled.span`
  color: ${p => p.theme.text};
  word-break: break-word;
`;

const ParentLink = styled.span`
  display: inline-block;
  color: ${p => p.theme.accent};
  background: ${p => p.theme.accentLight};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;

const EmptyArea = styled.div`
  padding: 24px 14px;
  text-align: center;
  color: ${p => p.theme.textMuted};
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const EmptyIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${p => p.theme.bgInput};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.textMuted};

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-top: 1px solid ${p => p.theme.border};
  font-size: 12px;
  color: ${p => p.theme.text};
`;

const ItemThumb = styled.div`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  margin-right: 10px;
  border-radius: 6px;
  background: ${p => p.theme.bgInput};
  border: 1px solid ${p => p.theme.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ItemThumbPlaceholder = styled.span`
  font-size: 9px;
  color: ${p => p.theme.textMuted};
  text-align: center;
  line-height: 1.2;
`;

const ImageUploadBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 1px dashed ${p => p.theme.border};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
`;

const ImagePreview = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .empty {
    font-size: 10px;
    color: ${p => p.theme.textMuted};
    text-align: center;
    padding: 4px;
  }
`;

const FileLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.accent};
  border: 1px solid ${p => p.theme.accent};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover { background: ${p => p.theme.accentLight}; }

  input { display: none; }
`;

const Checkbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${p => p.theme.text};
  cursor: pointer;
`;

const ItemRowName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
`;

const ItemRowPrice = styled.span`
  font-weight: 700;
  color: ${p => p.theme.accent};
  white-space: nowrap;
`;

const Footer = styled.div`
  border-top: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  padding: 12px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FooterButton = styled.button`
  padding: 8px 18px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.textSecondary};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.bgHover};
    color: ${p => p.theme.text};
  }
`;

// 모달
const ModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${p => p.theme.bgCard};
  padding: 22px;
  border-radius: ${p => p.theme.radius};
  box-shadow: ${p => p.theme.shadowLg};
  z-index: 3000;
  min-width: 360px;
  max-width: 480px;
  border: 1px solid ${p => p.theme.border};

  @media screen and (max-width: 480px) {
    width: calc(100vw - 24px);
    min-width: 0;
    padding: 16px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: ${p => p.theme.text};
  font-size: 12px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  box-sizing: border-box;
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const FormTextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  min-height: 70px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  box-sizing: border-box;
  background-color: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ModalButton = styled.button`
  padding: 8px 16px;
  border: ${p => p.$primary ? 'none' : `1px solid ${p.theme.border}`};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};
  background: ${p => p.$primary ? p.theme.accent : p.theme.bgCard};
  color: ${p => p.$primary ? '#fff' : p.theme.text};

  &:hover {
    background: ${p => p.$primary ? p.theme.accentHover : p.theme.bgHover};
  }
`;

// SVG 아이콘
const IconFolder = () => (
  <svg viewBox="0 0 24 24"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" /></svg>
);
const IconPencil = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const IconLayers = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 3l9 5-9 5-9-5 9-5z" />
    <path d="M3 13l9 5 9-5" />
    <path d="M3 18l9 5 9-5" />
  </svg>
);
const IconInbox = () => (
  <svg viewBox="0 0 24 24">
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

// ============== 데이터 어댑터 ==============
// 팀 데이터 → AdminDashboard 와 동일한 형태로 변환
// - 팀 카테고리 중 is_root=true 인 것들이 "그룹" 역할 (없으면 첫 카테고리)
// - 모든 팀 카테고리는 모든 그룹의 categoriesByGroup 에 들어감 (전체 가시성)
// - next_category_id → next_category(이름) 변환
function adaptTeamTree(treeData) {
  const cats = treeData?.categories || [];
  const items = treeData?.items || [];
  const catById = {};
  cats.forEach(c => { catById[c.category_id] = c; });

  // 그룹 = is_root 카테고리들 (없으면 첫 cat 으로 fallback, 그것도 없으면 빈 배열)
  let rootCats = cats.filter(c => c.is_root);
  if (rootCats.length === 0 && cats.length > 0) {
    rootCats = [cats[0]];
  }

  const groups = rootCats.map(c => ({
    std_key: `cat-${c.category_id}`,
    std_value: c.main_category,
    rootCategoryId: c.category_id,
    rootCategoryName: c.main_category,
    info: c.info,
  }));

  // categoriesByGroup: 모든 cat 을 모든 그룹에 노출
  const adminCats = cats.map(c => ({
    main_category: c.main_category,
    info: c.info,
    category_type: c.category_type,
    _category_id: c.category_id,
    _is_root: c.is_root,
    _has_image: c.has_image,
  }));
  const categoriesByGroup = {};
  groups.forEach(g => {
    categoriesByGroup[g.std_key] = adminCats;
  });

  // itemsByCategory: 각 그룹별 키로 동일한 아이템 노출
  const itemsByCategory = {};
  groups.forEach(g => {
    cats.forEach(c => {
      const groupItems = items
        .filter(it => it.category_id === c.category_id)
        .map(it => {
          const next = it.next_category_id ? catById[it.next_category_id] : null;
          return {
            item_name: it.item_name,
            item_price: it.item_price,
            item_description: it.item_description,
            next_category: next ? next.main_category : null,
            image_url: it.image_url || null,
            _item_id: it.item_id,
            _category_id: it.category_id,
            _next_category_id: it.next_category_id,
            _has_image: c.has_image,
          };
        });
      itemsByCategory[`${g.std_key}::${c.main_category}`] = groupItems;
    });
  });

  return {
    groups,
    categoriesByGroup,
    itemsByCategory,
    rawCats: cats,
    rawItems: items,
    catById,
  };
}

// ============== 컴포넌트 ==============

export default function TeamTreeManager({ isOpen, onClose, team }) {
  const [treeData, setTreeData] = useState(null);
  const [adapted, setAdapted] = useState({
    groups: [], categoriesByGroup: {}, itemsByCategory: {},
    rawCats: [], rawItems: [], catById: {},
  });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [itemEditModal, setItemEditModal] = useState(null);
  const [itemEditForm, setItemEditForm] = useState({});
  const bumpTreeVersion = useSetRecoilState(TeamTreeVersionAtom);

  const myRole = team?.my_role || 'member';
  const priceUnit = team?.price_unit || '만원';

  // 모든 mutation 후 호출 — 외부 화면(TeamWizardApp 등) 에 트리 변경 알림
  const notifyChanged = useCallback(() => {
    bumpTreeVersion(v => v + 1);
  }, [bumpTreeVersion]);

  const reload = useCallback(async () => {
    if (!team?.team_id) return;
    try {
      const data = await getTeamTree(team.team_id);
      setTreeData(data);
      const a = adaptTeamTree(data);
      setAdapted(a);
      // 그룹 선택 유지 (이름 매칭) 또는 첫 그룹
      setSelectedGroup(prev => {
        if (prev && a.groups.find(g => g.std_key === prev.std_key)) {
          return a.groups.find(g => g.std_key === prev.std_key);
        }
        return a.groups[0] || null;
      });
      // 카테고리 선택 유지 (이름 매칭) — adminCat 형태 ( _category_id 포함 ) 로 보존
      setSelectedCategory(prev => {
        if (!prev) return null;
        const firstGroupKey = Object.keys(a.categoriesByGroup)[0];
        if (!firstGroupKey) return null;
        return (a.categoriesByGroup[firstGroupKey] || [])
          .find(c => c.main_category === prev.main_category) || null;
      });
    } catch (e) {
      console.error('[TeamTreeManager] reload', e);
    }
  }, [team?.team_id]);

  useEffect(() => {
    if (isOpen && team?.team_id) {
      reload();
    } else if (!isOpen) {
      setSelectedCategory(null);
      setSearch('');
    }
  }, [isOpen, team?.team_id, reload]);

  const { groups, categoriesByGroup, itemsByCategory, rawCats, catById } = adapted;

  // 그룹이 선택되고 wizard root 가 있는데 우측 selectedCategory 가 비어있으면 자동 선택
  useEffect(() => {
    if (!selectedCategory && selectedGroup) {
      const cats = categoriesByGroup[selectedGroup.std_key] || [];
      const target = cats.find(c => c.main_category === selectedGroup.rootCategoryName) || cats[0];
      if (target) setSelectedCategory(target);
    }
  }, [selectedCategory, selectedGroup, categoriesByGroup]);

  // 현재 그룹의 카테고리들
  const currentCategories = useMemo(() => {
    if (!selectedGroup) return [];
    const list = categoriesByGroup[selectedGroup.std_key] || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(c =>
      (c.main_category || '').toLowerCase().includes(q) ||
      (c.info || '').toLowerCase().includes(q)
    );
  }, [selectedGroup, categoriesByGroup, search]);

  // wizard 시작점 = 선택된 그룹의 root category 이름
  const wizardRoot = useMemo(() => {
    if (!selectedGroup) return null;
    return selectedGroup.rootCategoryName || null;
  }, [selectedGroup]);

  // 트리 관계 (parentMap, parentItemMap, childrenMap, allParentsMap)
  const treeRelations = useMemo(() => {
    if (!selectedGroup) return { parentMap: {}, parentItemMap: {}, childrenMap: {}, allParentsMap: {} };
    const parentMap = {};
    const parentItemMap = {};
    const childrenMap = {};
    const allParentsMap = {};
    rawCats.forEach(c => {
      const groupItems = (itemsByCategory[`${selectedGroup.std_key}::${c.main_category}`]) || [];
      groupItems.forEach(it => {
        if (!it.next_category) return;
        if (it.next_category !== wizardRoot && !parentMap[it.next_category]) {
          parentMap[it.next_category] = c.main_category;
          parentItemMap[it.next_category] = it.item_name;
        }
        if (!childrenMap[c.main_category]) childrenMap[c.main_category] = [];
        childrenMap[c.main_category].push({
          childName: it.next_category,
          viaItemName: it.item_name,
        });
        if (!allParentsMap[it.next_category]) allParentsMap[it.next_category] = [];
        allParentsMap[it.next_category].push({
          parentName: c.main_category,
          viaItemName: it.item_name,
        });
      });
    });
    return { parentMap, parentItemMap, childrenMap, allParentsMap };
  }, [selectedGroup, rawCats, itemsByCategory, wizardRoot]);

  // wizard 시작점에서 도달 가능한 카테고리 집합
  const wizardReachable = useMemo(() => {
    const reachable = new Set();
    if (!wizardRoot) return reachable;
    reachable.add(wizardRoot);
    const queue = [wizardRoot];
    while (queue.length > 0) {
      const cur = queue.shift();
      const children = treeRelations.childrenMap[cur] || [];
      children.forEach(c => {
        if (!reachable.has(c.childName)) {
          reachable.add(c.childName);
          queue.push(c.childName);
        }
      });
    }
    return reachable;
  }, [wizardRoot, treeRelations]);

  const currentItems = useMemo(() => {
    if (!selectedGroup || !selectedCategory) return [];
    const key = `${selectedGroup.std_key}::${selectedCategory.main_category}`;
    return itemsByCategory[key] || [];
  }, [selectedGroup, selectedCategory, itemsByCategory]);

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setSelectedCategory(null);
    setSearch('');
  };

  // ===== 모달 오픈 핸들러 =====
  const openAddGroupModal = () => {
    // 새 "대주제" = is_root=true 인 카테고리
    setEditModal({ type: 'group' });
    setEditForm({ name: '', description: '', categoryType: 'S', hasImage: false });
  };
  const openAddCategoryModal = () => {
    if (!selectedGroup) return;
    setEditModal({ type: 'category' });
    setEditForm({ name: '', description: '', categoryType: 'S', hasImage: false });
  };
  const openAddItemModal = () => {
    if (!selectedGroup || !selectedCategory) return;
    setEditModal({ type: 'item' });
    setEditForm({ name: '', description: '', price: '', image_url: '' });
  };
  const openEditCategoryModal = () => {
    if (!selectedCategory) return;
    setEditModal({
      type: 'category',
      isEdit: true,
      original: selectedCategory,
    });
    setEditForm({
      name: selectedCategory.main_category,
      description: selectedCategory.info || '',
      categoryType: selectedCategory.category_type || 'S',
      hasImage: !!selectedCategory._has_image,
    });
  };

  // ===== 저장 =====
  const handleSave = async () => {
    if (!editForm.name?.trim()) {
      alert('이름을 입력해주세요.');
      return;
    }
    try {
      if (editModal.type === 'group') {
        // 새 대주제 = is_root=true 인 카테고리
        await addCategory(team.team_id, {
          main_category: editForm.name.trim(),
          info: editForm.description || '',
          category_type: editForm.categoryType || 'S',
          is_root: true,
          has_image: !!editForm.hasImage,
        });
      } else if (editModal.type === 'category' && editModal.isEdit) {
        await updateCategory(team.team_id, editModal.original._category_id, {
          main_category: editForm.name.trim(),
          info: editForm.description || '',
          category_type: editForm.categoryType || 'S',
          is_root: editModal.original._is_root || false,
          has_image: !!editForm.hasImage,
        });
      } else if (editModal.type === 'category') {
        await addCategory(team.team_id, {
          main_category: editForm.name.trim(),
          info: editForm.description || '',
          category_type: editForm.categoryType || 'S',
          is_root: false,
          has_image: !!editForm.hasImage,
        });
      } else if (editModal.type === 'item') {
        if (!editForm.price && editForm.price !== 0 && editForm.price !== '0') {
          alert('가격을 입력해주세요.');
          return;
        }
        await addItem(team.team_id, {
          category_id: selectedCategory._category_id,
          item_name: editForm.name.trim(),
          item_price: Number(editForm.price) || 0,
          item_description: editForm.description || '',
          next_category_id: null,
          image_url: editForm.image_url || null,
        });
      }
      alert('저장되었습니다.');
      setEditModal(null);
      await reload();
      notifyChanged();
    } catch (e) {
      console.error('[handleSave]', e);
      alert(`저장에 실패했습니다.\n${e.message || ''}`);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    const itemCount = currentItems.length;
    const msg = itemCount > 0
      ? `"${selectedCategory.main_category}" 카테고리를 삭제합니다.\n` +
        `이 카테고리에 속한 ${itemCount}개의 하위 항목과,\n` +
        `이 카테고리를 가리키는 다른 항목들의 연결도 함께 끊깁니다.\n\n계속하시겠습니까?`
      : `"${selectedCategory.main_category}" 카테고리를 삭제하시겠습니까?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteCategory(team.team_id, selectedCategory._category_id);
      alert('카테고리가 삭제되었습니다.');
      setSelectedCategory(null);
      await reload();
      notifyChanged();
    } catch (e) {
      console.error('[handleDeleteCategory]', e);
      alert(`삭제 실패: ${e.message || ''}`);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`"${item.item_name}" 항목을 삭제하시겠습니까?`)) return;
    try {
      await deleteItem(team.team_id, item._item_id);
      alert('삭제되었습니다.');
      await reload();
      notifyChanged();
    } catch (e) {
      console.error('[handleDeleteItem]', e);
      alert(`삭제 실패: ${e.message || ''}`);
    }
  };

  // 항목 편집 모달
  const openItemEditModal = (item) => {
    setItemEditForm({
      name: item.item_name,
      price: item.item_price,
      description: item.item_description || '',
      next_category_id: item._next_category_id || '',
      image_url: item.image_url || '',
      has_image: !!item._has_image,
    });
    setItemEditModal({ original: item });
  };

  const handleItemEditSave = async () => {
    const original = itemEditModal?.original;
    if (!original) return;
    try {
      await updateItem(team.team_id, original._item_id, {
        item_name: original.item_name,
        item_price: Number(itemEditForm.price) || 0,
        item_description: itemEditForm.description || '',
        next_category_id: itemEditForm.next_category_id ? Number(itemEditForm.next_category_id) : null,
        image_url: itemEditForm.image_url || null,
      });
      alert('저장되었습니다.');
      setItemEditModal(null);
      await reload();
      notifyChanged();
    } catch (e) {
      console.error('[handleItemEditSave]', e);
      alert(`저장 실패: ${e.message || ''}`);
    }
  };

  // 이미지 파일 → 파일서버 업로드 → URL 을 form 에 세팅
  const [uploadingImage, setUploadingImage] = useState(false);
  const handleUploadImage = async (file, target) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadImageToFileServer(file);
      if (target === 'add') {
        setEditForm(prev => ({ ...prev, image_url: url }));
      } else {
        setItemEditForm(prev => ({ ...prev, image_url: url }));
      }
    } catch (e) {
      alert(`이미지 업로드 실패: ${e.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  if (!isOpen || !team) return null;

  // 좌측 그룹 카드 렌더
  const renderGroupCard = (group) => {
    const isSelected = selectedGroup?.std_key === group.std_key;
    const cats = categoriesByGroup[group.std_key];
    const count = cats ? cats.length : '·';
    return (
      <SelectableCard
        key={group.std_key}
        $selected={isSelected}
        onClick={() => handleSelectGroup(group)}
      >
        <FolderIcon $selected={isSelected}><IconFolder /></FolderIcon>
        <CardBody>
          <CardName>{group.std_value}</CardName>
          <CardDesc>{group.info || '시작점 카테고리'}</CardDesc>
        </CardBody>
        <CardCount>{count}</CardCount>
      </SelectableCard>
    );
  };

  const detailNode = selectedCategory;
  const detailItemCount = currentItems.length;

  return (
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <TitleIcon><IconLayers /></TitleIcon>
            트리 관리자
            <TitleSub>— {team.name}</TitleSub>
            <RoleBadge>{(myRole || '').toUpperCase()}</RoleBadge>
          </Title>
          <CloseX onClick={onClose}>×</CloseX>
        </Header>

        <InfoBar>대주제를 선택하면 해당 그룹의 하위 트리를 관리할 수 있습니다.</InfoBar>

        <Body>
          {/* 좌측 - 대주제 */}
          <Column>
            <ColumnHeader>
              <ColumnTitle>대주제 선택</ColumnTitle>
            </ColumnHeader>
            <ScrollArea>
              {groups.length > 0 ? (
                <SectionGroup>
                  <SectionGroupLabel>팀 트리</SectionGroupLabel>
                  {groups.map(renderGroupCard)}
                </SectionGroup>
              ) : (
                <EmptyArea style={{ paddingTop: 24 }}>
                  <EmptyIcon><IconInbox /></EmptyIcon>
                  <div>등록된 대주제가 없습니다.<br />아래 버튼으로 시작하세요.</div>
                </EmptyArea>
              )}
              <FullAddButton onClick={openAddGroupModal}>+ 새 대주제 추가</FullAddButton>
            </ScrollArea>
          </Column>

          {/* 중앙 - 하위 트리 (카테고리) */}
          <Column>
            <ColumnHeader>
              <ColumnTitle>
                선택된 대주제의 진행 단계
                <CountBadge>{(categoriesByGroup[selectedGroup?.std_key] || []).length}개 카테고리</CountBadge>
              </ColumnTitle>
              <OutlineAddButton
                onClick={openAddCategoryModal}
                disabled={!selectedGroup}
              >+ 새 트리 추가</OutlineAddButton>
            </ColumnHeader>
            <div style={{ padding: '12px 12px 0' }}>
              <SearchInput
                placeholder="카테고리 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {selectedGroup && (categoriesByGroup[selectedGroup.std_key] || []).length > 0 && (() => {
                const cats = categoriesByGroup[selectedGroup.std_key] || [];
                const wizardRootExists = wizardRoot && cats.some(c => c.main_category === wizardRoot);
                const orphanCats = cats.filter(c => !wizardReachable.has(c.main_category));
                const reachableCount = wizardRootExists
                  ? Array.from(wizardReachable).filter(n => cats.some(c => c.main_category === n)).length
                  : 0;
                const totalCount = cats.length;
                if (!wizardRootExists) {
                  return (
                    <InfoStrip style={{ background: '#fef3c7', color: '#92400e' }}>
                      ⚠ Wizard 시작점 카테고리가 없습니다. 대주제를 다시 선택하거나 추가하세요.
                    </InfoStrip>
                  );
                }
                return (
                  <InfoStrip>
                    견적서 wizard는 <strong>{wizardRoot}</strong>에서 시작해 항목 선택 시 next_category 따라 진행 ·{' '}
                    {totalCount}개 중 <strong>{reachableCount}개</strong> 도달 가능
                    {orphanCats.length > 0 && (
                      <>, <span className="warn">{orphanCats.length}개 고아</span> (wizard 진입 불가 — 다른 항목의 next_category로 연결하거나 삭제해주세요)</>
                    )}
                  </InfoStrip>
                );
              })()}
            </div>
            {/* 검색 중: 평면 매칭 리스트, 그 외: 흐름도 */}
            {search ? (
              <ScrollArea>
                {currentCategories.length === 0 ? (
                  <EmptyArea>
                    <EmptyIcon><IconInbox /></EmptyIcon>
                    <div>검색 결과가 없습니다.</div>
                  </EmptyArea>
                ) : (
                  currentCategories.map(cat => {
                    const isSelected = selectedCategory?.main_category === cat.main_category;
                    const itemKey = `${selectedGroup.std_key}::${cat.main_category}`;
                    const itemCount = (itemsByCategory[itemKey] || []).length;
                    return (
                      <SelectableCard
                        key={cat.main_category}
                        $selected={isSelected}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        <FolderIcon $selected={isSelected}><IconLayers /></FolderIcon>
                        <CardBody>
                          <CardName>{cat.main_category}</CardName>
                          <CardDesc>{cat.info || '설명 없음'}</CardDesc>
                        </CardBody>
                        <CardCount>{itemCount}</CardCount>
                      </SelectableCard>
                    );
                  })
                )}
              </ScrollArea>
            ) : (categoriesByGroup[selectedGroup?.std_key] || []).length === 0 ? (
              <ScrollArea>
                <EmptyArea>
                  <EmptyIcon><IconInbox /></EmptyIcon>
                  <div>
                    {selectedGroup
                      ? '하위 항목이 없습니다.'
                      : '왼쪽에서 대주제를 선택하세요.'}
                  </div>
                </EmptyArea>
              </ScrollArea>
            ) : (
              <DrillDownView
                categories={categoriesByGroup[selectedGroup.std_key] || []}
                itemsByCategory={itemsByCategory}
                groupKey={selectedGroup.std_key}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
                onLinkNext={openItemEditModal}
                wizardRoot={wizardRoot}
                wizardReachable={wizardReachable}
                priceUnit={priceUnit}
              />
            )}
            <DragHint>항목 카드를 클릭하면 다음 카테고리로 이동, 상단 빵부스러기로 되돌아가기</DragHint>
          </Column>

          {/* 우측 - 상세 정보 */}
          <Column>
            <ColumnHeader>
              <ColumnTitle>현재 트리 상세</ColumnTitle>
              <DetailHeaderActions>
                <SmallActionButton
                  disabled={!detailNode}
                  onClick={openEditCategoryModal}
                  title="현재 트리 정보 수정 — 이름 변경 시 연결된 next_category 참조도 자동으로 같이 갱신됩니다"
                >수정</SmallActionButton>
                <SmallActionButton
                  $danger
                  disabled={!detailNode}
                  onClick={handleDeleteCategory}
                  title="현재 트리 삭제"
                >삭제</SmallActionButton>
              </DetailHeaderActions>
            </ColumnHeader>
            <ScrollArea>
              {!detailNode ? (
                <EmptyArea style={{ paddingTop: 60 }}>
                  <EmptyIcon><IconInbox /></EmptyIcon>
                  <div>중앙에서 하위 트리를 선택하세요.</div>
                </EmptyArea>
              ) : (
                <>
                  <DetailCard>
                    <DetailCardTitle>기본 정보</DetailCardTitle>
                    <DetailGrid>
                      <DetailKey>이름</DetailKey>
                      <DetailValue>{detailNode.main_category}</DetailValue>
                      <DetailKey>설명</DetailKey>
                      <DetailValue>{detailNode.info || '-'}</DetailValue>
                      <DetailKey>상위 그룹</DetailKey>
                      <DetailValue>
                        <ParentLink>{selectedGroup?.std_value}</ParentLink>
                      </DetailValue>
                      <DetailKey>항목 수</DetailKey>
                      <DetailValue>{detailItemCount}</DetailValue>
                      <DetailKey>선택 타입</DetailKey>
                      <DetailValue>
                        {detailNode.category_type === 'M' ? '멀티 선택' : '싱글 선택'}
                      </DetailValue>
                    </DetailGrid>
                  </DetailCard>

                  {/* 트리 부모 / 자식 관계 */}
                  {(() => {
                    const findCatByName = (name) =>
                      rawCats.find(c => c.main_category === name);

                    const buildPath = (name) => {
                      const path = [name];
                      let cursor = name;
                      const visited = new Set([name]);
                      while (treeRelations.parentMap[cursor]) {
                        const parent = treeRelations.parentMap[cursor];
                        if (visited.has(parent)) break;
                        visited.add(parent);
                        path.unshift(parent);
                        cursor = parent;
                      }
                      return path;
                    };
                    const path = buildPath(detailNode.main_category);
                    const isOnWizardChain = wizardReachable.has(detailNode.main_category);
                    const children = treeRelations.childrenMap[detailNode.main_category] || [];
                    const allParents = treeRelations.allParentsMap[detailNode.main_category] || [];

                    return (
                      <DetailCard>
                        <DetailCardTitle>트리 관계</DetailCardTitle>
                        <DetailGrid>
                          <DetailKey>Wizard 상태</DetailKey>
                          <DetailValue>
                            {detailNode.main_category === wizardRoot ? (
                              <StatusBadge $variant="start">시작점</StatusBadge>
                            ) : isOnWizardChain ? (
                              <span style={{ fontSize: 12, color: '#047857' }}>도달 가능</span>
                            ) : (
                              <StatusBadge $variant="orphan">고아 (진입 불가)</StatusBadge>
                            )}
                          </DetailValue>
                          <DetailKey>경로</DetailKey>
                          <DetailValue>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                              {path.map((p, i) => (
                                <React.Fragment key={p}>
                                  {i > 0 && <span style={{ color: '#94a3b8', fontSize: 11 }}>›</span>}
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      const c = findCatByName(p);
                                      if (c) {
                                        const adminLikeCat = (categoriesByGroup[selectedGroup.std_key] || []).find(x => x.main_category === c.main_category);
                                        if (adminLikeCat) setSelectedCategory(adminLikeCat);
                                      }
                                    }}
                                  >
                                    <ParentLink>{p}</ParentLink>
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          </DetailValue>
                          <DetailKey>진입 가능한<br/>모든 부모</DetailKey>
                          <DetailValue>
                            {allParents.length === 0 ? (
                              <span style={{ color: '#94a3b8', fontSize: 12 }}>없음 (트리 루트 또는 고아)</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {allParents.map((p, i) => (
                                  <span
                                    key={`${p.parentName}-${p.viaItemName}-${i}`}
                                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                                    onClick={() => {
                                      const adminLikeCat = (categoriesByGroup[selectedGroup.std_key] || []).find(x => x.main_category === p.parentName);
                                      if (adminLikeCat) setSelectedCategory(adminLikeCat);
                                    }}
                                  >
                                    <ParentLink>{p.parentName}</ParentLink>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                      ({p.viaItemName} 다음)
                                    </span>
                                  </span>
                                ))}
                                {allParents.length > 1 && (
                                  <span style={{ fontSize: 11, color: '#0f766e', marginTop: 2 }}>
                                    · 총 {allParents.length}개 경로로 진입 가능
                                  </span>
                                )}
                              </div>
                            )}
                          </DetailValue>
                          <DetailKey>하위 트리</DetailKey>
                          <DetailValue>
                            {children.length === 0 ? (
                              <span style={{ color: '#94a3b8', fontSize: 12 }}>연결된 하위 트리 없음</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {children.map((c, i) => (
                                  <span
                                    key={i}
                                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    onClick={() => {
                                      const adminLikeCat = (categoriesByGroup[selectedGroup.std_key] || []).find(x => x.main_category === c.childName);
                                      if (adminLikeCat) setSelectedCategory(adminLikeCat);
                                    }}
                                  >
                                    <ParentLink>{c.childName}</ParentLink>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                      ({c.viaItemName} 선택 시)
                                    </span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </DetailValue>
                        </DetailGrid>
                      </DetailCard>
                    );
                  })()}

                  <DetailCard>
                    <DetailCardTitle>하위 항목 ({detailItemCount})</DetailCardTitle>
                    {detailItemCount === 0 ? (
                      <EmptyArea>
                        <EmptyIcon><IconInbox /></EmptyIcon>
                        <div>등록된 하위 항목이 없습니다.</div>
                        <PrimaryAddButton onClick={openAddItemModal}>+ 하위 항목 추가</PrimaryAddButton>
                      </EmptyArea>
                    ) : (
                      <>
                        {currentItems.map((item, idx) => (
                          <ItemRow key={idx}>
                            {detailNode._has_image && (
                              <ItemThumb>
                                {item.image_url ? (
                                  <img src={item.image_url} alt={item.item_name} />
                                ) : (
                                  <ItemThumbPlaceholder>사진<br/>없음</ItemThumbPlaceholder>
                                )}
                              </ItemThumb>
                            )}
                            <ItemRowName>{item.item_name}</ItemRowName>
                            <ItemRowPrice>{formatNumber(item.item_price)}{priceUnit}</ItemRowPrice>
                            <SmallActionButton
                              style={{ marginLeft: 8, padding: '3px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => openItemEditModal(item)}
                              title="편집 (가격/설명/다음 카테고리)"
                            ><IconPencil />수정</SmallActionButton>
                            <SmallActionButton
                              $danger
                              style={{ marginLeft: 4, padding: '3px 8px', fontSize: 11 }}
                              onClick={() => handleDeleteItem(item)}
                            >삭제</SmallActionButton>
                          </ItemRow>
                        ))}
                        <div style={{ padding: 12, borderTop: '1px solid var(--border, #e2e8f0)' }}>
                          <PrimaryAddButton onClick={openAddItemModal}>+ 하위 항목 추가</PrimaryAddButton>
                        </div>
                      </>
                    )}
                  </DetailCard>
                </>
              )}
            </ScrollArea>
          </Column>
        </Body>

        <Footer>
          <FooterButton onClick={onClose}>닫기</FooterButton>
        </Footer>

        {editModal && (
          <ModalWrap onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {editModal.type === 'group' && '새 대주제 추가'}
              {editModal.type === 'category' && (editModal.isEdit ? '현재 트리 수정' : '새 트리 추가')}
              {editModal.type === 'item' && '하위 항목 추가'}
            </ModalTitle>
            <FormGroup>
              <FormLabel>이름 *</FormLabel>
              <FormInput
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="이름을 입력하세요"
                autoFocus
              />
            </FormGroup>
            {editModal.type === 'item' && (
              <FormGroup>
                <FormLabel>가격 ({priceUnit}) *</FormLabel>
                <FormInput
                  type="text"
                  value={editForm.price ? formatNumber(editForm.price) : ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/,/g, '');
                    if (!isNaN(v)) setEditForm({ ...editForm, price: v });
                  }}
                  placeholder="가격을 입력하세요"
                />
              </FormGroup>
            )}
            {(editModal.type === 'category' || editModal.type === 'group') && (
              <>
                <FormGroup>
                  <FormLabel>선택 타입 *</FormLabel>
                  <FormSelect
                    value={editForm.categoryType || 'S'}
                    onChange={(e) => setEditForm({ ...editForm, categoryType: e.target.value })}
                  >
                    <option value="S">싱글 선택 (하나만 선택 가능)</option>
                    <option value="M">멀티 선택 (여러 개 선택 가능)</option>
                  </FormSelect>
                </FormGroup>
                <FormGroup>
                  <Checkbox>
                    <input
                      type="checkbox"
                      checked={!!editForm.hasImage}
                      onChange={(e) => setEditForm({ ...editForm, hasImage: e.target.checked })}
                    />
                    사진 포함 (요식업/실사 메뉴 등 — 하위 항목에 사진 등록 가능)
                  </Checkbox>
                </FormGroup>
              </>
            )}
            {editModal.type === 'item' && selectedCategory?._has_image && (
              <FormGroup>
                <FormLabel>사진 (선택)</FormLabel>
                <ImageUploadBox>
                  <ImagePreview>
                    {editForm.image_url ? (
                      <img src={editForm.image_url} alt="" />
                    ) : (
                      <span className="empty">사진<br/>없음</span>
                    )}
                  </ImagePreview>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <FileLabel>
                      {uploadingImage ? '업로드 중...' : (editForm.image_url ? '사진 변경' : '사진 선택')}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingImage}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUploadImage(f, 'add');
                          e.target.value = '';
                        }}
                      />
                    </FileLabel>
                    {editForm.image_url && (
                      <SmallActionButton
                        $danger
                        style={{ fontSize: 11 }}
                        onClick={() => setEditForm({ ...editForm, image_url: '' })}
                      >제거</SmallActionButton>
                    )}
                  </div>
                </ImageUploadBox>
              </FormGroup>
            )}
            <FormGroup>
              <FormLabel>설명</FormLabel>
              <FormTextArea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="설명을 입력하세요"
              />
            </FormGroup>
            <ModalButtons>
              <ModalButton onClick={() => setEditModal(null)}>취소</ModalButton>
              <ModalButton $primary onClick={handleSave}>
                {editModal.isEdit ? '수정' : '추가'}
              </ModalButton>
            </ModalButtons>
          </ModalWrap>
        )}

        {itemEditModal && (
          <ModalWrap onClick={(e) => e.stopPropagation()}>
            <ModalTitle>하위 항목 편집 — {itemEditModal.original.item_name}</ModalTitle>
            <FormGroup>
              <FormLabel>이름</FormLabel>
              <FormInput value={itemEditForm.name || ''} disabled />
            </FormGroup>
            <FormGroup>
              <FormLabel>가격 ({priceUnit}) *</FormLabel>
              <FormInput
                type="text"
                value={itemEditForm.price ? formatNumber(itemEditForm.price) : ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/,/g, '');
                  if (!isNaN(v)) setItemEditForm({ ...itemEditForm, price: v });
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>설명</FormLabel>
              <FormTextArea
                value={itemEditForm.description || ''}
                onChange={(e) => setItemEditForm({ ...itemEditForm, description: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>다음 카테고리 (이 항목 선택 시 표시될 하위 트리)</FormLabel>
              <FormSelect
                value={itemEditForm.next_category_id || ''}
                onChange={(e) => setItemEditForm({ ...itemEditForm, next_category_id: e.target.value })}
              >
                <option value="">— 없음 —</option>
                {rawCats
                  .filter(c => c.category_id !== itemEditModal.original._category_id)
                  .map(c => (
                    <option key={c.category_id} value={c.category_id}>{c.main_category}</option>
                  ))}
              </FormSelect>
            </FormGroup>
            {itemEditForm.has_image && (
              <FormGroup>
                <FormLabel>사진</FormLabel>
                <ImageUploadBox>
                  <ImagePreview>
                    {itemEditForm.image_url ? (
                      <img src={itemEditForm.image_url} alt="" />
                    ) : (
                      <span className="empty">사진<br/>없음</span>
                    )}
                  </ImagePreview>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <FileLabel>
                      {uploadingImage ? '업로드 중...' : (itemEditForm.image_url ? '사진 변경' : '사진 선택')}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingImage}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUploadImage(f, 'edit');
                          e.target.value = '';
                        }}
                      />
                    </FileLabel>
                    {itemEditForm.image_url && (
                      <SmallActionButton
                        $danger
                        style={{ fontSize: 11 }}
                        onClick={() => setItemEditForm({ ...itemEditForm, image_url: '' })}
                      >제거</SmallActionButton>
                    )}
                  </div>
                </ImageUploadBox>
              </FormGroup>
            )}
            <ModalButtons>
              <ModalButton onClick={() => setItemEditModal(null)}>취소</ModalButton>
              <ModalButton $primary onClick={handleItemEditSave}>저장</ModalButton>
            </ModalButtons>
          </ModalWrap>
        )}
      </Container>
    </Overlay>
  );
}
